/**
 * "Book" — the same booking dialog, reachable from anywhere.
 *
 * Debra's ask, in her words: "we also need to have a way to hit schedule
 * anywhere from inside software not just from calendar view". The front desk
 * books people while they're standing there; making them detour via the
 * calendar first is the kind of small friction that pushes a practice back to
 * pen and paper.
 *
 * The menu and provider list load only when the dialog is opened, so putting
 * this button on every screen costs nothing until it's used.
 */
import { useState } from "react";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Button, useToast } from "@/components/ui";
import { displayDayKey } from "@/lib/format";
import type { Contact, Provider, Service } from "@/lib/types";
import { AppointmentFormModal } from "./AppointmentFormModal";

export function BookButton({
  contact = null,
  label = "Book",
  variant = "primary",
  size = "sm",
  className,
  onBooked,
}: {
  /** Pre-select this client (used on a client's record). */
  contact?: Contact | null;
  label?: string;
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md";
  className?: string;
  onBooked?: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);

  async function openDialog() {
    if (loading) return;
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get<{ services: Service[] }>("/services"),
        api.get<{ providers: Provider[] }>("/providers"),
      ]);
      setServices(s.services.filter((x) => !x.archived));
      setProviders(p.providers);
      setOpen(true);
    } catch {
      toast.error("Couldn't open the booking form. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={() => void openDialog()} disabled={loading}>
        <Plus className="h-4 w-4" />
        {label}
      </Button>
      {open && (
        <AppointmentFormModal
          mode={{ kind: "create", contact }}
          services={services}
          providers={providers}
          defaultDate={displayDayKey(new Date())}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); onBooked?.(); }}
        />
      )}
    </>
  );
}
